<template>
  <div class="filter-editor">
    <hr-filter-chip-group
      :model-value="modelValue.filterCriteriaType"
      :options="operators"
      @update:model-value="onOperatorChange"
    />

    <!-- One row: a range reads as a single value, and stacking it buries the pane. -->
    <div
      v-if="isBetween"
      class="filter-editor__range"
    >
      <component
        :is="valueField"
        :model-value="rangeValue('from')"
        :label="i18n.t('common.labelFrom')"
        v-bind="fieldProps"
        @update:model-value="onRangeChange('from', $event)"
      />
      <div class="filter-editor__conjunction">
        {{ i18n.t('common.and') }}
      </div>
      <component
        :is="valueField"
        :model-value="rangeValue('to')"
        :label="i18n.t('common.labelTo')"
        v-bind="fieldProps"
        @update:model-value="onRangeChange('to', $event)"
      />
    </div>

    <!-- The pane headline already names the criterion, so the field does not repeat it. -->
    <component
      :is="valueField"
      v-else
      :model-value="singleValue"
      v-bind="fieldProps"
      @update:model-value="onSingleChange"
    />
  </div>
</template>

<script setup>
import { filterPropertyTypes } from 'boot/globals';
import { i18n } from 'boot/i18n';
import { computed } from 'vue';

import { HrDurationField, HrFilterChipGroup, HrTextField } from 'src/design-system';

import { getNumericCriteriaTypes } from '../criteriaTypes';
import { BETWEEN } from '../filterStatement';

/**
 * Numbers and durations. Both are an operator plus either one value or a range, and they differ
 * only in the field that collects the value.
 */
defineOptions({ name: 'NumericEditor' });

const props = defineProps({
  /** The criterion being edited. */
  criterion: {
    type: Object,
    required: true,
  },
  /** The statement being built for it. */
  modelValue: {
    type: Object,
    required: true,
  },
});

const emit = defineEmits(['update:modelValue']);

/*
 * The short forms, which is what the chip has room for: spelled out, "less than or equal to" and
 * "greater than or equal to" wrap the row onto a second line. They are also what the filter's own
 * label uses, so the chip picked reads the same as the chip produced.
 */
const operators = getNumericCriteriaTypes()
  .map((option) => ({ ...option, label: option.shortLabel }));

const isDuration = computed(() => props.criterion.filterType === filterPropertyTypes.Duration);
const isBetween = computed(() => props.modelValue.filterCriteriaType === BETWEEN);

/*
 * A duration is collected in H:MM:SS and handed back in seconds by HrDurationField, so both
 * branches deal in numbers and nothing here has to know about the display format.
 */
const valueField = computed(() => (isDuration.value ? HrDurationField : HrTextField));

/*
 * The rules say why the filter cannot be applied. Without them a decimal or a negative leaves
 * Apply disabled with nothing on screen explaining it, which reads as the dialog being broken.
 * They mirror canApplyFilter's Number branch, so the field and the gate cannot disagree.
 */
const numberRules = [
  (value) => value === null || value === '' || Number(value) >= 0
    || i18n.t('common.validatorMustBeGreaterOrEqualZero'),
  (value) => value === null || value === '' || Number.isInteger(Number(value))
    || i18n.t('common.validatorMustBeInteger'),
];

const fieldProps = computed(() => (isDuration.value
  ? {}
  : { type: 'number', rules: numberRules, lazyRules: true }));

const singleValue = computed(() => (typeof props.modelValue.filteredItems === 'number'
  ? props.modelValue.filteredItems
  : null));

const rangeValue = (end) => {
  const value = props.modelValue.filteredItems?.[end];
  return typeof value === 'number' ? value : null;
};

function patch(changes) {
  emit('update:modelValue', { ...props.modelValue, ...changes });
}

/*
 * A number field hands back a string; a duration field hands back seconds already. Empty is null
 * rather than 0, so a cleared field does not read as a deliberate zero.
 *
 * What the user typed is carried through as it is. Rounding and clamping it here silently filtered
 * on a different number than the one on screen: a distance of 1.9 became 1. Whole and
 * non-negative is a real constraint on these criteria, and canApplyFilter holds the filter back
 * until it is met, but that is the field's business to state rather than this function's to
 * impose. The dialog this replaces said so too, with these same two rules.
 */
function toNumber(value) {
  if (isDuration.value) {
    return typeof value === 'number' ? value : null;
  }
  if (value === null || value === '') {
    return null;
  }

  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

/*
 * Switching to or from between changes the shape of the value, from a number to a pair. Clearing
 * it on the way avoids a half-migrated statement where a range is read as a single number.
 */
function onOperatorChange(value) {
  const changesShape = (value === BETWEEN) !== isBetween.value;

  patch({
    filterCriteriaType: value,
    ...(changesShape && { filteredItems: value === BETWEEN ? {} : null }),
  });
}

function onSingleChange(value) {
  patch({ filteredItems: toNumber(value) });
}

function onRangeChange(end, value) {
  patch({
    filteredItems: {
      ...(props.modelValue.filteredItems ?? {}),
      [end]: toNumber(value),
    },
  });
}
</script>

<style lang="scss" scoped>
.filter-editor {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.filter-editor__range {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
}

/* The fields share the width; the word between them takes only what it needs. */
.filter-editor__range > *:not(.filter-editor__conjunction) {
  flex: 1 1 8rem;
  min-width: 0;
}

.filter-editor__conjunction {
  flex: none;
  color: var(--app-on-surface-variant);
}
</style>
